import React, { useEffect, useRef, useState } from "react";
import { Copy, ImagePlus, Save, Send, Share2, Trash2 } from "lucide-react";
import {
  createShare,
  deleteShare,
  fileUrl,
  getShareMessage,
  listShares,
  saveShareMessage,
} from "../lib/api";
import { compressImage } from "../lib/imageCompress";

export default function PhotoSharePanel() {
  const [message, setMessage] = useState("");
  const [defaultMsg, setDefaultMsg] = useState("");
  const [savedMessage, setSavedMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [shares, setShares] = useState([]);
  const [guestName, setGuestName] = useState("");
  const [pickedFiles, setPickedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const fileRef = useRef(null);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  // "/?share=<id>" (not "/share/<id>") — works on static hosts without SPA fallback
  const shareLink = (s) => `${origin}/?share=${s.id}`;

  useEffect(() => {
    getShareMessage().then((d) => {
      setMessage(d.message || "");
      setSavedMessage(d.message || "");
      setDefaultMsg(d.default || "");
    }).catch(() => {});
    listShares().then(setShares).catch(() => {});
  }, []);

  const buildMsg = (s) =>
    (savedMessage || defaultMsg)
      .replaceAll("{name}", s.guest_name)
      .replaceAll("{link}", shareLink(s));

  const onSaveMessage = async () => {
    setSaving(true);
    try {
      const d = await saveShareMessage(message);
      setMessage(d.message);
      setSavedMessage(d.message);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save message");
    }
    setSaving(false);
  };

  const onPick = (e) => {
    setPickedFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const onCreate = async () => {
    if (!guestName.trim() || !pickedFiles.length) return;
    setUploading(true);
    setError("");
    try {
      const compressed = await Promise.all(pickedFiles.map((f) => compressImage(f)));
      const s = await createShare(guestName.trim(), compressed);
      setShares((prev) => [s, ...prev]);
      setGuestName("");
      setPickedFiles([]);
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this share? The link will stop working.")) return;
    try {
      await deleteShare(id);
      setShares((prev) => prev.filter((s) => s.id !== id));
    } catch { /* ignore */ }
  };

  const copyLink = async (s) => {
    try {
      await navigator.clipboard.writeText(shareLink(s));
      setCopiedId(s.id);
      setTimeout(() => setCopiedId((c) => (c === s.id ? null : c)), 2000);
    } catch { /* ignore */ }
  };

  const sendWa = (s) => {
    window.open(`https://wa.me/?text=${encodeURIComponent(buildMsg(s))}`, "_blank", "noopener");
  };

  const dirty = message !== savedMessage;

  return (
    <section className="mb-8 glass-deep rounded-3xl p-5 sm:p-7" data-testid="photo-share-panel">
      <div className="flex items-center gap-2 mb-1">
        <Share2 size={20} className="text-emerald-700" />
        <h2 className="font-display text-xl text-ink">Guest Photo Sharing (WhatsApp)</h2>
      </div>
      <p className="text-xs text-ink-soft mb-4 max-w-3xl">
        Upload photos of a guest, get a private link, and send it on WhatsApp. The guest opens
        the link, sees their photos and downloads them. Customize the message below — use{" "}
        <code className="px-1 bg-white/60 rounded">{"{name}"}</code> for the guest's name and{" "}
        <code className="px-1 bg-white/60 rounded">{"{link}"}</code> for their photo link.
      </p>

      {/* Message template */}
      <div className="bg-white/70 rounded-2xl p-4 mb-4">
        <p className="label-eyebrow mb-2">WhatsApp message (with {"{name}"} and {"{link}"})</p>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={4}
          className="w-full bg-transparent border border-[#D4AF37]/40 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]"
          style={{ fontFamily: "'Noto Sans Telugu',sans-serif" }}
          data-testid="share-msg-input"
        />
        <div className="flex items-center gap-3 mt-2">
          <button
            onClick={onSaveMessage}
            disabled={saving || !dirty}
            className="btn-royal text-xs py-1.5 px-4 disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="share-msg-save"
          >
            <Save size={12} /> {saving ? "Saving…" : "Save Message"}
          </button>
          {message !== defaultMsg && (
            <button
              onClick={() => setMessage(defaultMsg)}
              className="text-xs text-ink-soft hover:underline"
              data-testid="share-msg-reset"
            >
              Reset to default
            </button>
          )}
        </div>
      </div>

      {/* Upload new share */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          type="text"
          value={guestName}
          onChange={(e) => setGuestName(e.target.value)}
          placeholder="Guest name"
          className="bg-white/70 border border-stone-300 rounded-lg px-3 py-1.5 text-xs w-44 focus:outline-none focus:border-[#D4AF37]"
          data-testid="share-guest-name-input"
        />
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} data-testid="share-file-input" />
        <button
          onClick={() => fileRef.current?.click()}
          className="btn-ghost-royal text-xs py-1.5 px-4 inline-flex items-center gap-1.5"
          data-testid="share-pick-btn"
        >
          <ImagePlus size={12} /> {pickedFiles.length ? `${pickedFiles.length} photo${pickedFiles.length === 1 ? "" : "s"} picked` : "Pick Photos"}
        </button>
        <button
          onClick={onCreate}
          disabled={uploading || !guestName.trim() || !pickedFiles.length}
          className="btn-royal text-xs py-1.5 px-4 disabled:opacity-40"
          data-testid="share-create-btn"
        >
          {uploading ? "Uploading…" : "Upload & Create Link"}
        </button>
        <span className="text-xs text-ink-soft">{shares.length} share{shares.length === 1 ? "" : "s"}</span>
      </div>

      {error && <p className="text-rose-700 text-xs mb-3" data-testid="share-error">{error}</p>}

      {/* Shares list */}
      <ul className="space-y-2" data-testid="share-list">
        {shares.map((s) => (
          <li
            key={s.id}
            className="flex flex-wrap items-center gap-3 bg-white/70 border border-stone-200 rounded-xl px-3 py-2"
            data-testid={`share-row-${s.id}`}
          >
            <img
              src={fileUrl(s.photos[0])}
              alt={s.guest_name}
              className="w-12 h-12 rounded-lg object-cover ring-1 ring-[#D4AF37] shrink-0"
            />
            <div className="min-w-0 grow">
              <p className="text-sm font-medium text-ink truncate">{s.guest_name}</p>
              <p className="text-[11px] text-ink-soft font-mono truncate">
                {s.photos.length} photo{s.photos.length === 1 ? "" : "s"} · {shareLink(s)}
              </p>
            </div>
            <a
              href={shareLink(s)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-plum hover:underline shrink-0"
              data-testid={`share-open-${s.id}`}
            >
              Open
            </a>
            <button
              onClick={() => copyLink(s)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] bg-stone-100 hover:bg-stone-200 text-ink shrink-0"
              data-testid={`share-copy-${s.id}`}
            >
              <Copy size={10} /> {copiedId === s.id ? "Copied!" : "Copy Link"}
            </button>
            <button
              onClick={() => sendWa(s)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] bg-emerald-600 text-white hover:bg-emerald-700 shrink-0"
              data-testid={`share-wa-${s.id}`}
            >
              <Send size={10} /> WhatsApp
            </button>
            <button
              onClick={() => onDelete(s.id)}
              className="text-rose-600 hover:text-rose-800 shrink-0 p-1"
              title="Delete share"
              data-testid={`share-delete-${s.id}`}
            >
              <Trash2 size={13} />
            </button>
          </li>
        ))}
        {!shares.length && (
          <li className="text-center py-5 text-ink-soft text-xs">
            No shares yet — enter a guest name, pick their photos, and create a link.
          </li>
        )}
      </ul>
    </section>
  );
}
